import React, { useState } from 'react'
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
import { saveOpportunity } from '../../../ts/data'
import toast from 'react-hot-toast'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'

interface BookedQuoteNotificationProps {
   onUnbook?: () => void
}

export default function BookedQuoteNotification({
   onUnbook,
}: BookedQuoteNotificationProps) {
   const state = useQuoteContext()
   const [isConfirmMode, setIsConfirmMode] = useState(false)
   const [isProcessing, setIsProcessing] = useState(false)
   const [show, setShow] = useState(true)

   // Use the new useFeaturePermissions hook
   const { featurePermissions, hasPermission } = useFeaturePermissions()

   // Check if user has UnbookQuote permission
   const hasUnbookPermission = hasPermission('UnbookQuote', state.user_role)

   // Only show if stage is booked
   if (state.stage !== 'booked') {
      return null
   }

   // Get original stage from search params for unbook operation
   const getOriginalStageFromParams = () => {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('stage') || ''
   }

   // Handle unbook button click
   const handleUnbookClick = async () => {
      if (!isConfirmMode) {
         // First click - switch to confirmation mode
         setIsConfirmMode(true)
      } else {
         // Second click - proceed with unbooking
         if (isProcessing) return

         setIsProcessing(true)
         const toastId = toast.loading('Unbooking Quote...')

         try {
            // Get original stage from search params
            const originalStage = getOriginalStageFromParams()

            // Save the opportunity with original stage
            const saveResponse = await saveOpportunity({
               state: state,
               stage: originalStage,
               hasUnlimitedDatePermission: true, // Unbook must never be blocked
            })

            if (saveResponse && saveResponse.ok) {
               // Update stage state and unlock fieldset
               state.setstage(originalStage)
               state.setIsClosed(false)
               toast.success('Quote unbooked successfully!', { id: toastId })

               // Call optional callback
               if (onUnbook) {
                  onUnbook()
               }
            } else {
               const errorText = saveResponse
                  ? await saveResponse.text()
                  : 'Unknown error'
               throw new Error(`Failed to unbook quote: ${errorText}`)
            }
         } catch (error) {
            console.error('Error unbooking quote:', error)
            toast.error(
               error instanceof Error
                  ? error.message
                  : 'An error occurred while unbooking the quote.',
               { id: toastId },
            )
         } finally {
            setIsProcessing(false)
            setIsConfirmMode(false)
         }
      }
   }

   // Handle button blur to reset confirmation state
   const handleButtonBlur = () => {
      if (isConfirmMode && !isProcessing) {
         setIsConfirmMode(false)
      }
   }

   // Handle modal close
   const handleClose = () => {
      setShow(false)
      setIsConfirmMode(false)
   }

   return (
      <Modal
         show={show}
         onHide={handleClose}
         centered={true}
         size='lg'
         backdrop='static'
      >
         <Modal.Header closeButton>
            <Modal.Title className='w-100 text-center'>
               <i className='fas fa-lock me-2'></i>
               Quote is Booked
            </Modal.Title>
         </Modal.Header>
         <Form noValidate>
            <Modal.Body>
               <div className='text-center mb-3'>
                  <i
                     className='fas fa-lock'
                     style={{ fontSize: '3rem', color: '#ffc107' }}
                  ></i>
               </div>
               <p className='text-center mb-3'>
                  This quote is already booked and can no longer be modified.
               </p>
               {hasUnbookPermission && (
                  <div className='d-flex justify-content-center w-100'>
                     <Button
                        variant={isConfirmMode ? 'warning' : 'primary'}
                        onClick={handleUnbookClick}
                        onBlur={handleButtonBlur}
                        disabled={isProcessing}
                     >
                        {isProcessing
                           ? 'Processing...'
                           : isConfirmMode
                             ? 'Are you sure?'
                             : 'Unbook Quote'}
                     </Button>
                  </div>
               )}
            </Modal.Body>
         </Form>
      </Modal>
   )
}
